import Header from 'components/headers/addLoan'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { getFinancialDashboard } from 'services/fund'

const toman = (value: number) =>
    `${Number(value || 0).toLocaleString('fa-IR')} تومان`

function Row({ label, value }: { label: string; value: number }) {
    return (
        <div className=" flex justify-between items-center py-2 border-b border-dashed border-gray-200 last:border-0">
            <span className=" text-sm text-gray-1">{label}</span>
            <span className=" text-sm font-bold">{toman(value)}</span>
        </div>
    )
}

export default function FinancialDashboard() {
    const { id } = useParams()
    const { data, isLoading } = useQuery(
        ['financial-dashboard', id],
        getFinancialDashboard
    )

    const statusText: { [key: string]: string } = {
        deficit: 'کسری',
        surplus: 'مازاد',
        balanced: 'تراز',
    }

    return (
        <div className=" mx-3 flex flex-col min-h-screen pb-6">
            <Header title="داشبورد هزینه و درآمد صندوق" />

            {isLoading && (
                <p className=" text-center text-gray-1 mt-10">در حال بارگذاری…</p>
            )}

            {!isLoading && data && (
                <div className=" mt-4">
                    {data.ledger_warning && (
                        <div className=" bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-lg p-3 text-xs text-center mb-3">
                            احتمال مغایرت در دفترکل این صندوق وجود دارد؛ اعداد زیر را
                            با احتیاط بررسی کنید.
                        </div>
                    )}

                    {/* Headline: cost-coverage balance */}
                    <div
                        className={` rounded-lg p-4 text-center shadow-sm ${
                            data.cost_coverage_balance < 0
                                ? 'bg-[#FFEDED]'
                                : 'bg-[#EDFFE8]'
                        }`}
                    >
                        <p className=" text-sm text-gray-1">
                            تراز هزینه و درآمد صندوق
                        </p>
                        <p
                            className={` text-2xl font-bold mt-1 ${
                                data.cost_coverage_balance < 0
                                    ? 'text-error-3'
                                    : 'text-green-1'
                            }`}
                        >
                            {toman(data.cost_coverage_balance)}
                        </p>
                        <p className=" text-xs text-gray-1 mt-1">
                            وضعیت: {statusText[data.status] ?? data.status}
                        </p>
                        {data.cost_coverage_balance < 0 && (
                            <p className=" text-xs text-error-3 mt-2">
                                هزینه‌های صندوق بیش از درآمدهای آن بوده و بخشی از
                                سرمایهٔ اعضا صرف هزینه‌ها شده است.
                            </p>
                        )}
                    </div>

                    {/* Income */}
                    <div className=" bg-white rounded-lg p-4 mt-3 shadow-sm">
                        <p className=" text-sm text-green-1 mb-1">درآمدهای صندوق</p>
                        <Row
                            label="سود مصوب وام‌ها (کارمزد)"
                            value={data.income.loan_interest_accrued}
                        />
                        <p className=" text-[11px] text-gray-1 -mt-1 mb-1">
                            از این مبلغ، {toman(
                                data.income.loan_interest_realized
                            )}{' '}
                            تا کنون دریافت شده و باقیِ آن هنوز وصول نشده است.
                        </p>
                        <Row
                            label="هبه برای پوشش هزینه"
                            value={data.income.hibah_expense}
                        />
                    </div>

                    {/* Expenses */}
                    <div className=" bg-white rounded-lg p-4 mt-3 shadow-sm">
                        <p className=" text-sm text-error-3 mb-1">هزینه‌های صندوق</p>
                        <Row
                            label="برداشت بابت هزینه‌های صندوق"
                            value={data.expenses.box_cost_cashouts}
                        />
                    </div>

                    {/* Loanable donations + members capital */}
                    <div className=" bg-white rounded-lg p-4 mt-3 shadow-sm">
                        <Row
                            label="هبهٔ بلاعوض (قابل وام‌دهی)"
                            value={data.loanable_hibah_balance}
                        />
                        <Row
                            label="سرمایهٔ بازگرداندنی به اعضا"
                            value={data.members_capital}
                        />
                    </div>

                    {/* Sharia note */}
                    <div className=" bg-[#F4F8FF] rounded-lg p-3 mt-3 text-xs text-gray-1 leading-6">
                        {data.sharia_note}
                    </div>
                </div>
            )}
        </div>
    )
}
